import React, { useState, useEffect } from 'react';
import { DateRange } from 'react-date-range';
import 'react-date-range/dist/styles.css';
import 'react-date-range/dist/theme/default.css';
import {
    Box,
    TextField,
    Popover,
    Button,
    FormControl,
    Paper,
    Typography,
    Divider,
    Chip,
    Tooltip,
    alpha,
    Fade,
    IconButton,
    Stack
} from '@mui/material';
import { styled, useTheme } from '@mui/material/styles';
import { ptBR } from 'date-fns/locale';
import { format, subDays, startOfWeek, endOfWeek, subMonths, startOfMonth, endOfMonth, subYears, startOfYear, endOfYear, isToday, isYesterday } from 'date-fns';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import DateRangeIcon from '@mui/icons-material/DateRange';
import TodayIcon from '@mui/icons-material/Today';
import EventIcon from '@mui/icons-material/Event';
import ViewWeekIcon from '@mui/icons-material/ViewWeek';
import ViewModuleIcon from '@mui/icons-material/ViewModule';
import CalendarViewMonthIcon from '@mui/icons-material/CalendarViewMonth';
import CloseIcon from '@mui/icons-material/Close';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ClearIcon from '@mui/icons-material/Clear';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';

// Estilizando componentes com refinamentos
const StyledPaper = styled(Paper)(({ theme }) => ({
    padding: 0,
    boxShadow: '0 10px 40px rgba(0, 0, 0, 0.1)',
    borderRadius: theme.shape.borderRadius * 1.5,
    width: 820,
    maxWidth: '100%',
    overflow: 'hidden',
    transition: 'all 0.25s ease-in-out',
}));

const StyledButton = styled(Button)(({ theme }) => ({
    textTransform: 'none',
    fontSize: '0.75rem',
    borderRadius: theme.shape.borderRadius * 0.75,
    fontWeight: 500,
    padding: theme.spacing(0.5, 1),
    border: `1px solid ${alpha(theme.palette.primary.main, 0.15)}`,
    backgroundColor: 'transparent',
    '&:hover': {
        backgroundColor: alpha(theme.palette.primary.main, 0.08),
        borderColor: theme.palette.primary.main,
    },
    '&.Mui-selected': {
        backgroundColor: alpha(theme.palette.primary.main, 0.1),
        borderColor: theme.palette.primary.main,
        color: theme.palette.primary.main,
        fontWeight: 600,
        '&:hover': {
            backgroundColor: alpha(theme.palette.primary.main, 0.15),
        },
    },
    transition: 'all 0.2s ease-in-out',
}));

const StyledButtonStack = styled(Stack)(({ theme }) => ({
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(0.6),
}));

const StyledTextField = styled(TextField)(({ theme }) => ({
    '& .MuiOutlinedInput-root': {
        borderRadius: theme.shape.borderRadius * 1.5,
        transition: 'all 0.2s ease-in-out',
        backgroundColor: alpha(theme.palette.background.default, 0.6),
        '&:hover': {
            backgroundColor: alpha(theme.palette.background.default, 0.9),
        },
        '&.Mui-focused': {
            backgroundColor: 'white',
            boxShadow: '0 0 0 2px rgba(63, 81, 181, 0.15)',
        },
        '&:hover fieldset': {
            borderColor: theme.palette.primary.main,
        },
        padding: '0px 5px'
    },
    '& .MuiInputLabel-root': {
        color: theme.palette.text.secondary,
        fontWeight: 500,
    },
    '& .MuiInputBase-input': {
        fontSize: '14px',
    },
}));

const DateChip = styled(Chip)(({ theme }) => ({
    backgroundColor: alpha(theme.palette.primary.main, 0.08),
    color: theme.palette.primary.main,
    fontWeight: 500,
    borderRadius: theme.shape.borderRadius * 0.75,
    height: '28px',
    border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
    '& .MuiChip-icon': {
        color: theme.palette.primary.main,
    },
}));

const ApplyButton = styled(Button)(({ theme }) => ({
    borderRadius: theme.shape.borderRadius * 0.75,
    fontWeight: 600,
    padding: theme.spacing(0.75, 2),
    boxShadow: '0 4px 8px rgba(63, 81, 181, 0.1)',
    transition: 'all 0.2s ease-in-out',
    backgroundColor: theme.palette.primary.main,
    '&:hover': {
        boxShadow: '0 6px 12px rgba(63, 81, 181, 0.2)',
        backgroundColor: theme.palette.primary.dark,
        transform: 'translateY(-1px)',
    },
}));

const ClearButton = styled(Button)(({ theme }) => ({
    borderRadius: theme.shape.borderRadius * 0.75,
    fontWeight: 500,
    padding: theme.spacing(0.75, 1.5),
    marginRight: theme.spacing(1),
    color: theme.palette.text.secondary,
    backgroundColor: alpha(theme.palette.divider, 0.08),
    '&:hover': {
        backgroundColor: alpha(theme.palette.divider, 0.2),
    },
}));

// Componente personalizado para o cabeçalho do popover
const PopoverHeader = styled(Box)(({ theme }) => ({
    padding: theme.spacing(1.25, 2),
    borderBottom: `1px solid ${theme.palette.divider}`,
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    backgroundColor: alpha(theme.palette.primary.main, 0.03),
}));

// Estilização do calendário
const StyledDateRange = styled(Box)(({ theme }) => ({
    '& .rdrCalendarWrapper': {
        fontSize: '13px',
        width: '100%',
    },
    '& .rdrMonthAndYearWrapper': {
        height: '40px',
    },
    '& .rdrMonth': {
        width: '100%',
        padding: '0 0.4rem 0 0.4rem',
    },
    '& .rdrDay': {
        height: '34px',
    },
    '& .rdrDayNumber': {
        fontSize: '13px',
        fontWeight: 500,
    },
    '& .rdrMonthName': {
        fontSize: '13px',
        fontWeight: 600,
        paddingBottom: '4px',
    },
    '& .rdrStartEdge, .rdrEndEdge': {
        color: `${theme.palette.primary.main} !important`,
    },
    '& .rdrInRange': {
        color: `${alpha(theme.palette.primary.main, 0.2)} !important`,
    },
    '& .rdrDayToday .rdrDayNumber span:after': {
        background: theme.palette.primary.main,
    },
    // Adiciona transição suave nos dias ao passar o mouse
    '& .rdrDay:not(.rdrDayPassive) .rdrInRange ~ .rdrDayNumber span, .rdrDay:not(.rdrDayPassive) .rdrStartEdge ~ .rdrDayNumber span, .rdrDay:not(.rdrDayPassive) .rdrEndEdge ~ .rdrDayNumber span, .rdrDay:not(.rdrDayPassive) .rdrSelected ~ .rdrDayNumber span': {
        transition: 'all 0.2s ease-in-out',
    },
    // Melhora o hover nos dias
    '& .rdrDay:not(.rdrDayPassive):hover .rdrDayNumber span': {
        borderColor: theme.palette.primary.main,
    },
}));

const PopoverContent = styled(Box)(({ theme }) => ({
    display: 'flex',
    height: '100%',
}));

const Label = styled(Typography)(({ theme }) => ({
    marginLeft: '10px',
    fontSize: '0.75rem',
    fontWeight: 600,
    color: theme.palette.text.secondary,
}));

const SidebarContent = styled(Box)(({ theme }) => ({
    width: '230px',
    padding: theme.spacing(1.75),
    borderRight: `1px solid ${theme.palette.divider}`,
    backgroundColor: alpha(theme.palette.primary.main, 0.02),
}));

const MainContent = styled(Box)(({ theme }) => ({
    flex: 1,
    padding: theme.spacing(1.25),
    backgroundColor: 'white',
}));

const FooterContent = styled(Box)(({ theme }) => ({
    padding: theme.spacing(1.25, 1.75),
    borderTop: `1px solid ${theme.palette.divider}`,
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    backgroundColor: alpha(theme.palette.background.default, 0.5),
}));

const DateTypeButton = styled(Button)(({ theme, selected }) => ({
    justifyContent: 'flex-start',
    textTransform: 'none',
    borderRadius: theme.shape.borderRadius * 0.75,
    padding: theme.spacing(0.65, 1.25),
    marginBottom: theme.spacing(0.4),
    backgroundColor: selected ? alpha(theme.palette.primary.main, 0.1) : 'transparent',
    color: selected ? theme.palette.primary.main : theme.palette.text.primary,
    fontWeight: selected ? 600 : 400,
    fontSize: '0.8rem',
    borderLeft: selected ? `3px solid ${theme.palette.primary.main}` : '3px solid transparent',
    '&:hover': {
        backgroundColor: selected ? alpha(theme.palette.primary.main, 0.15) : alpha(theme.palette.primary.main, 0.05),
    },
}));

const SectionTitle = styled(Typography)(({ theme }) => ({
    fontSize: '0.7rem',
    fontWeight: 600,
    color: theme.palette.text.secondary,
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    marginBottom: theme.spacing(0.75),
    marginTop: theme.spacing(1.5),
}));

// Componente principal
const DateRangePicker = ({ dateFilter, setDateFilter }) => {
    const theme = useTheme();


    const [anchorEl, setAnchorEl] = useState(null);
    const [displayText, setDisplayText] = useState('Selecione um período');
    const [selectedPreset, setSelectedPreset] = useState(null);

    const open = Boolean(anchorEl);
    const id = open ? 'date-range-popover' : undefined;

    // Verificar se tem filtros aplicados
    const hasFilters = Boolean(dateFilter.type) || Boolean(dateFilter.range.startDate && dateFilter.range.endDate);

    // Função para limpar todos os filtros
    const handleClearFilters = (e) => {
        if (e) {
            e.stopPropagation();  // Evitar que o clique abra o popover
        }
        setDateFilter({
            type: null,
            range: {
                startDate: null,
                endDate: null,
                key: 'selection'
            }
        });
        setSelectedPreset(null);
        setDisplayText('Selecione um período');
    };

    // Detectar qual preset corresponde ao estado atual
    useEffect(() => {
        if (dateFilter.range.startDate && dateFilter.range.endDate) {
            const startDate = dateFilter.range.startDate;
            const endDate = dateFilter.range.endDate;

            // Verificar se é hoje
            if (isToday(startDate) && isToday(endDate)) {
                setSelectedPreset('today');
                return;
            }

            // Verificar se é ontem
            if (isYesterday(startDate) && isYesterday(endDate)) {
                setSelectedPreset('yesterday');
                return;
            }

            // Tentar detectar semana, mês ou ano
            const today = new Date();
            const lastWeekStart = startOfWeek(subDays(today, 7));
            const lastWeekEnd = endOfWeek(subDays(today, 7));
            const lastMonthStart = startOfMonth(subMonths(today, 1));
            const lastMonthEnd = endOfMonth(subMonths(today, 1));
            const lastYearStart = startOfYear(subYears(today, 1));
            const lastYearEnd = endOfYear(subYears(today, 1));

            if (startDate.getTime() === lastWeekStart.getTime() && endDate.getTime() === lastWeekEnd.getTime()) {
                setSelectedPreset('lastWeek');
            } else if (startDate.getTime() === lastMonthStart.getTime() && endDate.getTime() === lastMonthEnd.getTime()) {
                setSelectedPreset('lastMonth');
            } else if (startDate.getTime() === lastYearStart.getTime() && endDate.getTime() === lastYearEnd.getTime()) {
                setSelectedPreset('lastYear');
            } else {
                setSelectedPreset(null);
            }
        }
    }, [dateFilter.range]);

    // Função para atualizar o texto de exibição
    useEffect(() => {
        if (dateFilter.range.startDate && dateFilter.range.endDate) {
            if (dateFilter.range.startDate.getTime() === dateFilter.range.endDate.getTime()) {
                setDisplayText(format(dateFilter.range.startDate, 'dd/MM/yyyy'));
            } else {
                setDisplayText(
                    `${format(dateFilter.range.startDate, 'dd/MM/yyyy')} - ${format(dateFilter.range.endDate, 'dd/MM/yyyy')}`
                );
            }
        } else {
            setDisplayText('Selecione um período');
        }
    }, [dateFilter.range]);

    // Handlers
    const handleClick = (event) => {
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    const handleApply = () => {
        handleClose();
    };

    const handleDateTypeChange = (newType) => {
        setDateFilter(prev => ({
            ...prev,
            type: newType
        }));
    };

    // Função para atualizar o intervalo de datas
    const handleDateRangeChange = (item) => {
        setDateFilter(prev => ({
            ...prev,
            range: item.selection
        }));
    };

    // Funções para os presets de datas
    const setToday = () => {
        const today = new Date();
        setDateFilter(prev => ({
            ...prev,
            range: {
                startDate: today,
                endDate: today,
                key: 'selection'
            }
        }));
        setSelectedPreset('today');
    };

    const setYesterday = () => {
        const yesterday = subDays(new Date(), 1);
        setDateFilter(prev => ({
            ...prev,
            range: {
                startDate: yesterday,
                endDate: yesterday,
                key: 'selection'
            }
        }));
        setSelectedPreset('yesterday');
    };

    const setLastWeek = () => {
        const today = new Date();
        const start = startOfWeek(subDays(today, 7));
        const end = endOfWeek(subDays(today, 7));
        setDateFilter(prev => ({
            ...prev,
            range: {
                startDate: start,
                endDate: end,
                key: 'selection'
            }
        }));
        setSelectedPreset('lastWeek');
    };

    const setLastMonth = () => {
        const today = new Date();
        const start = startOfMonth(subMonths(today, 1));
        const end = endOfMonth(subMonths(today, 1));
        setDateFilter(prev => ({
            ...prev,
            range: {
                startDate: start,
                endDate: end,
                key: 'selection'
            }
        }));
        setSelectedPreset('lastMonth');
    };

    const setLastYear = () => {
        const today = new Date();
        const start = startOfYear(subYears(today, 1));
        const end = endOfYear(subYears(today, 1));
        setDateFilter(prev => ({
            ...prev,
            range: {
                startDate: start,
                endDate: end,
                key: 'selection'
            }
        }));
        setSelectedPreset('lastYear');
    };

    // Textos para tipos de data
    const dateTypeLabels = {
        creation: 'Data de Criação',
        finalization: 'Data de Finalização',
        start: 'Data de Início',
        conclusion: 'Data de Conclusão'
    };

    // Ícones para os tipos de data
    const dateTypeIcons = {
        creation: <CalendarMonthIcon fontSize="small" sx={{ fontSize: 16 }} />,
        finalization: <CheckCircleIcon fontSize="small" sx={{ fontSize: 16 }} />,
        start: <EventIcon fontSize="small" sx={{ fontSize: 16 }} />,
        conclusion: <TodayIcon fontSize="small" sx={{ fontSize: 16 }} />
    };

    // Ícones para os presets
    const presetIcons = {
        today: <TodayIcon fontSize="small" sx={{ fontSize: 16 }} />,
        yesterday: <EventIcon fontSize="small" sx={{ fontSize: 16 }} />,
        lastWeek: <ViewWeekIcon fontSize="small" sx={{ fontSize: 16 }} />,
        lastMonth: <ViewModuleIcon fontSize="small" sx={{ fontSize: 16 }} />,
        lastYear: <CalendarViewMonthIcon fontSize="small" sx={{ fontSize: 16 }} />
    };

    // Obter descrição amigável da seleção atual
    const getFriendlyDateDescription = () => {
        if (selectedPreset === 'today') return 'Hoje';
        if (selectedPreset === 'yesterday') return 'Ontem';
        if (selectedPreset === 'lastWeek') return 'Última Semana';
        if (selectedPreset === 'lastMonth') return 'Último Mês';
        if (selectedPreset === 'lastYear') return 'Último Ano';

        if (dateFilter.range.startDate && dateFilter.range.endDate) {
            if (dateFilter.range.startDate.getTime() === dateFilter.range.endDate.getTime()) {
                return `${format(dateFilter.range.startDate, 'dd/MM/yyyy')}`;
            } else {
                return `${format(dateFilter.range.startDate, 'dd/MM/yyyy')} - ${format(dateFilter.range.endDate, 'dd/MM/yyyy')}`;
            }
        }

        return 'Selecione um período';
    };

    // Verificar se nenhum tipo de data foi selecionado, mas há um intervalo
    const showTypeWarning = !dateFilter.type && (dateFilter.range.startDate && dateFilter.range.endDate);

    return (
        <Box sx={{ width: '100%', mx: 'auto' }}>
            <Tooltip title={hasFilters ? 'Clique para editar ou limpar o período' : 'Clique para selecionar um período'} arrow>
                <StyledTextField
                    id="date-range"
                    value={displayText}
                    onClick={handleClick}
                    size="small"
                    InputProps={{
                        readOnly: true,
                        startAdornment:
                            <Box sx={{ alignItems: 'center', display: 'flex', width: '260px' }}>
                                <Label>{dateFilter.type ? `${dateTypeLabels[dateFilter.type]}` : `Período`}</Label>
                                <Divider sx={{ margin: '0px 10px 0px 10px' }} orientation="vertical" flexItem />
                            </Box>,
                        endAdornment: (
                            <>
                                {hasFilters && (
                                    <IconButton
                                        size="small"
                                        onClick={handleClearFilters}
                                        sx={{ mr: 0.5 }}
                                        aria-label="Limpar filtros"
                                    >
                                        <ClearIcon fontSize="small" />
                                    </IconButton>
                                )}
                                <DateRangeIcon color="action" sx={{ opacity: 0.7 }} />
                            </>
                        ),
                    }}
                    fullWidth
                    placeholder="Clique para selecionar um período"
                />
            </Tooltip>

            <Popover
                id={id}
                open={open}
                anchorEl={anchorEl}
                onClose={handleClose}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'left',
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'left',
                }}
                TransitionComponent={Fade}
                transitionDuration={250}
                PaperProps={{
                    sx: {
                        borderRadius: 3,
                        mt: 1,
                        overflow: 'hidden',
                        '&:before': {
                            content: '""',
                            display: 'block',
                            position: 'absolute',
                            top: 0,
                            left: 20,
                            width: 10,
                            height: 10,
                            bgcolor: 'background.paper',
                            transform: 'translateY(-50%) rotate(45deg)',
                            zIndex: 0,
                            boxShadow: '-3px -3px 5px rgba(0,0,0,0.02)',
                        }
                    }
                }}
            >
                <StyledPaper elevation={0}>
                    <PopoverHeader>
                        <Typography variant="subtitle1" fontWeight="600" color="primary">
                            Selecione o intervalo de datas
                        </Typography>
                        <Box>
                            {hasFilters && (
                                <Tooltip title="Limpar todos os filtros" arrow>
                                    <IconButton
                                        onClick={handleClearFilters}
                                        size="small"
                                        sx={{ mr: 1 }}
                                        color="primary"
                                    >
                                        <ClearIcon fontSize="small" />
                                    </IconButton>
                                </Tooltip>
                            )}
                            <IconButton onClick={handleClose} size="small">
                                <CloseIcon fontSize="small" />
                            </IconButton>
                        </Box>
                    </PopoverHeader>

                    <PopoverContent>
                        {/* Sidebar com opções à esquerda */}
                        <SidebarContent>
                            <SectionTitle>Tipo de Data</SectionTitle>

                            <FormControl fullWidth variant="outlined" size="small">
                                <Stack spacing={0.4}>
                                    {Object.keys(dateTypeLabels).map((key) => (
                                        <DateTypeButton
                                            key={key}
                                            selected={dateFilter.type === key}
                                            onClick={() => handleDateTypeChange(key)}
                                            startIcon={dateTypeIcons[key]}
                                            fullWidth
                                        >
                                            {dateTypeLabels[key]}
                                        </DateTypeButton>
                                    ))}
                                </Stack>
                            </FormControl>

                            <SectionTitle>Períodos Predefinidos</SectionTitle>

                            <StyledButtonStack>
                                <StyledButton
                                    onClick={setToday}
                                    startIcon={presetIcons.today}
                                    className={selectedPreset === 'today' ? 'Mui-selected' : ''}
                                    fullWidth
                                >
                                    Hoje
                                </StyledButton>
                                <StyledButton
                                    onClick={setYesterday}
                                    startIcon={presetIcons.yesterday}
                                    className={selectedPreset === 'yesterday' ? 'Mui-selected' : ''}
                                    fullWidth
                                >
                                    Ontem
                                </StyledButton>
                                <StyledButton
                                    onClick={setLastWeek}
                                    startIcon={presetIcons.lastWeek}
                                    className={selectedPreset === 'lastWeek' ? 'Mui-selected' : ''}
                                    fullWidth
                                >
                                    Última Semana
                                </StyledButton>
                                <StyledButton
                                    onClick={setLastMonth}
                                    startIcon={presetIcons.lastMonth}
                                    className={selectedPreset === 'lastMonth' ? 'Mui-selected' : ''}
                                    fullWidth
                                >
                                    Último Mês
                                </StyledButton>
                                <StyledButton
                                    onClick={setLastYear}
                                    startIcon={presetIcons.lastYear}
                                    className={selectedPreset === 'lastYear' ? 'Mui-selected' : ''}
                                    fullWidth
                                >
                                    Último Ano
                                </StyledButton>
                            </StyledButtonStack>
                        </SidebarContent>

                        {/* Conteúdo principal com calendário */}
                        <MainContent>
                            {showTypeWarning && (
                                <Box
                                    sx={{
                                        mb: 1.5,
                                        p: 1,
                                        backgroundColor: alpha(theme.palette.warning.light, 0.15),
                                        borderRadius: theme.shape.borderRadius * 0.75,
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 1
                                    }}
                                >
                                    <ErrorOutlineIcon fontSize="small" color="warning" sx={{ fontSize: 16 }} />
                                    <Typography variant="caption" color="text.secondary">
                                        Selecione um tipo de data para completar o filtro
                                    </Typography>
                                </Box>
                            )}

                            <StyledDateRange>
                                <DateRange
                                    editableDateInputs={true}
                                    onChange={handleDateRangeChange}
                                    moveRangeOnFirstSelection={false}
                                    ranges={[dateFilter.range]}
                                    locale={ptBR}
                                    months={2}
                                    direction="horizontal"
                                    showSelectionPreview={true}
                                    rangeColors={[theme.palette.primary.main]}
                                    showDateDisplay={false}
                                    weekdayDisplayFormat="EEEEE"
                                />
                            </StyledDateRange>
                        </MainContent>
                    </PopoverContent>

                    {/* Rodapé com informação do período e botão aplicar */}
                    <FooterContent>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <DateChip
                                icon={dateFilter.type ? dateTypeIcons[dateFilter.type] : <DateRangeIcon fontSize="small" sx={{ fontSize: 16 }} />}
                                label={`${dateFilter.type ? `${dateTypeLabels[dateFilter.type]}: ` : ''}${getFriendlyDateDescription()}`}
                                variant="outlined"
                                size="small"
                            />
                        </Box>

                        <Box>
                            {hasFilters && (
                                <ClearButton
                                    startIcon={<ClearIcon fontSize="small" />}
                                    onClick={handleClearFilters}
                                    disableElevation
                                    size="small"
                                >
                                    Limpar
                                </ClearButton>
                            )}
                            <ApplyButton
                                variant="contained"
                                color="primary"
                                onClick={handleApply}
                                disableElevation
                                size="small"
                                disabled={!dateFilter.type && (dateFilter.range.startDate && dateFilter.range.endDate)}
                            >
                                Aplicar
                            </ApplyButton>
                        </Box>
                    </FooterContent>
                </StyledPaper>
            </Popover>
        </Box>
    );
};

export default DateRangePicker;